export type LensId = "operations" | "mobility" | "flood" | "environment" | "earth" | "security" | "poverty" | "safety" | "vibes" | "executive" | "intelligence";

export type LayerId =
  // Municipality core
  | "municipality-boundary"
  | "municipality-buildings"
  | "road-network"
  | "municipal-flood-cameras"
  | "distance-grid"
  // Transit
  | "transit-stations"
  | "transit-lines"
  // Live ops
  | "traffic-heatmap"
  | "incidents-itic"
  | "incidents-city-reports"
  | "cctv-cameras"
  // Open data
  | "datago-points"
  // Civic
  | "civic-points"
  | "waterways"
  // Risk
  | "flood-risk-zones"
  // Yala — Deep South security
  | "conflict-incidents"
  | "conflict-choropleth"
  | "security-news"
  // Yala — outcome choropleths
  | "poverty-choropleth"
  // Yala — flood / hydrology
  | "flood-gauges"
  | "dam-status"
  | "river-buffer"
  | "shelters"
  | "flood-affected-districts"
  // Yala — circular-city signature + EO
  | "ring-roads"
  | "alphaearth-landcover"
  | "alphaearth-floodprone"
  // 3D Tiles
  | "tile3d-buildings"
  // Air quality
  | "air4thai-stations"
  // GISTDA
  | "gistda-pois"
  | "gistda-solar"
  | "gistda-landuse"
  // News
  | "news-pins"
  // Imagery
  | "satellite-esri"
  | "google-satellite"
  | "google-traffic"
  | "satellite-viirs-truecolor"
  | "satellite-night"
  | "satellite-imerg"
  | "satellite-aerosol"
  | "satellite-no2"
  | "satellite-true-color"
  | "satellite-himawari"
  | "satellite-ndvi"
  | "satellite-lst"
  | "satellite-flood"
  | "satellite-terrain"
  // Boundary + heritage — rendered from the Yala geo extracts. These have no
  // palette row of their own; they are driven by the lens and the boundary
  // toggle.
  | "building-roofs"
  | "municipality-boundary-line"
  | "municipality-boundary-fill"
  | "heritage-old-town"
  | "heritage-temple-spires";

export type MapViewState =
  | { kind: "lens"; lensId: LensId }
  | { kind: "custom"; label: string };

export function layerCanEnable(_id: LayerId): boolean {
  return true;
}

export interface Lens {
  id: LensId;
  label: string;
  describe: string;
  layers: LayerId[];
}

export const LENSES: Lens[] = [
  {
    id: "executive",
    label: "EXEC",
    describe:
      "Strategic — municipal boundary, the circular-city ring roads, high-res satellite, transit + open-data POIs. Focused on Yala City Municipality (~19 km²).",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "ring-roads",
      "satellite-esri",
      "transit-stations",
      "road-network",
      "datago-points",
      "gistda-pois",
    ],
  },
  {
    id: "operations",
    label: "OPS",
    describe: "Operations — every building in 3D, the ring-road grid, road network, civic POIs (hospitals/police/fire/schools/mosques/markets), live traffic, incidents, CCTV. The default day-to-day view for the municipality.",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "ring-roads",
      "road-network",
      "civic-points",
      "traffic-heatmap",
      "incidents-city-reports",
      "incidents-itic",
      "cctv-cameras",
      "gistda-pois",
      "news-pins",
    ],
  },
  {
    id: "security",
    label: "SEC",
    describe: "Deep South Security — AGGREGATE conflict intensity by province/district (ACLED / Deep South Watch lineage), shown as choropleth shading, never individual incident points, with neutral framing (violence has significantly decreased since 2013). Yala is one of three insurgency-affected border provinces under martial law + emergency decree. Pairs with the Data Atlas security module.",
    layers: [
      "municipality-boundary-line",
      "conflict-choropleth",
      "municipality-buildings",
      "civic-points",
      "road-network",
    ],
  },
  {
    id: "poverty",
    label: "POV",
    describe: "Poverty & Wellbeing — MPI poverty choropleth (TPMAP 20.83% / 208,274 poor) by district, the single highest-value outcome layer the live yaladashboard.com lacks. Pairs with the Data Atlas outcomes module.",
    layers: [
      "municipality-boundary-line",
      "poverty-choropleth",
      "civic-points",
      "road-network",
    ],
  },
  {
    id: "flood",
    label: "FLOOD",
    describe: "Flood — the headline risk. Pattani River corridor + buffer, real river/canal gauges + Bang Lang Dam telemetry (FloodDash), terrain-connected DEM planning scenarios, IMERG rainfall + MODIS flood detection, DDPM emergency shelters.",
    // Esri imagery + the flood-prone fill as the single colorizer + flood
    // vectors (risk zones, river buffer, gauges, dam). Tap IMERG rainfall to
    // swap the colorizer to live rain — it replaces the flood-prone fill, so the
    // map never shows competing blue + orange washes.
    layers: [
      "municipality-boundary-line",
      "satellite-esri",
      "municipality-buildings",
      "river-buffer",
      "waterways",
      "flood-gauges",
      "dam-status",
      "flood-risk-zones",
      "shelters",
      "municipal-flood-cameras",
      "flood-affected-districts",
    ],
  },
  {
    id: "mobility",
    label: "MOB",
    describe: "Mobility — the radial road network, ring roads, transit stations, traffic heatmap, iTIC events, CCTV. For dispatch + routing decisions around the circular core.",
    layers: [
      "municipality-boundary-line",
      "ring-roads",
      "road-network",
      "transit-lines",
      "transit-stations",
      "traffic-heatmap",
      "incidents-itic",
      "cctv-cameras",
    ],
  },
  {
    id: "environment",
    label: "ENV",
    describe: "Environment — Esri high-res satellite, flood-risk polygons, waterways, AlphaEarth land cover (rubber vs forest), air-quality stations, solar rooftop potential. Opt into MODIS NDVI/LST/AOD when zoomed out.",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "satellite-esri",
      "alphaearth-landcover",
      "flood-risk-zones",
      "waterways",
      "air4thai-stations",
      "gistda-solar",
    ],
  },
  {
    id: "earth",
    label: "EAR",
    describe: "EarthAlpha — earth-observation lens for rain, flood, heat, haze, greenery, land use, waterways, and AlphaEarth embeddings around Yala and the Bang Lang reservoir.",
    // One base (Esri) + ONE colorizer (NDVI greenery) by default. The other
    // earth-obs overlays (rain, heat, haze, NO₂, land cover) are one tap away in
    // the Imagery group and swap in cleanly — never five rasters at once.
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "satellite-esri",
      "satellite-ndvi",
      "waterways",
      "air4thai-stations",
    ],
  },
  {
    id: "safety",
    label: "SAF",
    describe: "Safety — flood-risk zones, citizen reports (Traffy), iTIC, CCTV, waterways for drainage, hospitals + fire + police, MODIS flood detection, conflict incidents, DDPM emergency shelters.",
    layers: [
      "municipality-boundary-line",
      "municipality-buildings",
      "civic-points",
      "waterways",
      "flood-risk-zones",
      "incidents-city-reports",
      "incidents-itic",
      "conflict-incidents",
      "cctv-cameras",
      "shelters",
      "flood-affected-districts",
    ],
  },
  {
    id: "vibes",
    label: "FLYOVER",
    describe: "City flyover — complete roofed Microsoft/OSM footprints over high-resolution satellite imagery. Roof shapes, colours and heights are estimates; not surveyed photogrammetry.",
    layers: ["municipality-boundary-line", "municipality-buildings", "satellite-esri", "waterways"],
  },
  {
    id: "intelligence",
    label: "INT",
    describe: "Integrated Intelligence — TimesFM rainfall/flood forecast alerts wired to Earth Observation. Click any forecast metric in the left rail to activate its map layer. Pairs with the Predictive Intelligence and Earth Obs panels.",
    layers: [
      "municipality-boundary-line",
      "satellite-imerg",
      "satellite-ndvi",
      "flood-risk-zones",
      "flood-gauges",
      "incidents-city-reports",
      "waterways",
    ],
  },
];

// ─── Layer exclusivity ─────────────────────────────────────────────────
// Some layers tint the ENTIRE map. Stacking several translucent ones turns the
// map into unreadable mud (rainfall blue + vegetation green + heat orange + a
// flood fill, all at ~70% opacity). So two groups behave like radio buttons:
// at most ONE base satellite and ONE full-area "colorize" overlay at a time.

/** The map background. Pick exactly one. */
export const SATELLITE_BASE_LAYERS: LayerId[] = [
  "satellite-esri",
  "satellite-true-color",
  "satellite-viirs-truecolor",
  "satellite-night",
  "satellite-himawari",
  "satellite-terrain",
];

/** Full-area data overlays that colour the whole map. Pick at most one. */
export const MAP_COLORIZE_LAYERS: LayerId[] = [
  "satellite-imerg",        // rainfall
  "satellite-ndvi",         // vegetation
  "satellite-lst",          // land temperature
  "satellite-aerosol",      // haze / AOD
  "satellite-no2",          // air pollution
  "satellite-flood",        // MODIS flood detection
  "alphaearth-landcover",   // land classification
  "alphaearth-floodprone",  // flood-prone areas
];

const EXCLUSIVE_GROUPS: LayerId[][] = [SATELLITE_BASE_LAYERS, MAP_COLORIZE_LAYERS];

/** The exclusive group a layer belongs to, or null if it stacks freely. */
export function exclusiveGroupOf(id: LayerId): LayerId[] | null {
  return EXCLUSIVE_GROUPS.find((g) => g.includes(id)) ?? null;
}

/**
 * Keep the map readable by allowing at most one member of each exclusive group.
 * `prefer` (e.g. a just-toggled layer) wins within its group; otherwise the
 * first one encountered in `ids` is kept.
 */
export function enforceLayerExclusivity(ids: Iterable<LayerId>, prefer?: LayerId): Set<LayerId> {
  const out = new Set<LayerId>(ids);
  for (const group of EXCLUSIVE_GROUPS) {
    const present = group.filter((g) => out.has(g));
    if (present.length <= 1) continue;
    const keep = prefer && present.includes(prefer) ? prefer : present[0];
    for (const g of present) if (g !== keep) out.delete(g);
  }
  return out;
}

// Yala is landlocked — there is no maritime group. The old coastal-fork layers
// (port / ferry / AIS / nav-aids / OpenSeaMap) are dropped from the palette.
export type LayerGroup = "municipality" | "security" | "mobility" | "incidents" | "open-data" | "imagery" | "environment";

export const LAYER_GROUP_LABEL: Record<LayerGroup, string> = {
  municipality: "Municipality",
  security:     "Deep South Security",
  mobility:     "Mobility",
  incidents:    "Incidents",
  "open-data":  "Open data",
  imagery:      "Imagery",
  environment:  "Environment",
};

// ─── Satellite freshness ───────────────────────────────────────────────
// NASA GIBS products have different latency between the satellite pass
// and tile publication. Numbers below are the typical "delay in days"
// from the user's local now → the publicly served tile.
const SATELLITE_DELAY_DAYS: Partial<Record<LayerId, number>> = {
  "satellite-true-color":         1,    // MODIS Terra ~24-36 h
  "satellite-viirs-truecolor":    1,    // VIIRS NOAA-20 ~24 h
  "satellite-night":              1,    // VIIRS Day/Night Band ~24 h
  "satellite-himawari":           0,    // Himawari Band 13 — 10 min
  "satellite-imerg":              0,    // IMERG half-hourly, ~6 h delay
  "satellite-ndvi":               8,    // MODIS NDVI 8-day composite
  "satellite-lst":                1,    // MODIS LST day ~36 h
  "satellite-aerosol":            1,    // MAIAC AOD ~24 h
  "satellite-no2":                1,    // OMI NO2 ~24 h
  "satellite-flood":              3,    // MODIS 3-day combined flood
  "satellite-esri":               0,    // Esri mosaic — not date-specific
  "satellite-terrain":            0,    // OpenTopoMap — vector, static
};

export function satelliteFreshness(id: LayerId): { label: string; date: string } | null {
  const d = SATELLITE_DELAY_DAYS[id];
  if (d == null) return null;
  const t = new Date();
  t.setUTCDate(t.getUTCDate() - d);
  const date = t.toISOString().slice(0, 10);
  if (d === 0) return { label: "LIVE", date };
  if (d === 1) return { label: "Y’DAY", date };
  return { label: `${d}D AGO`, date };
}

export const ALL_LAYERS: {
  id: LayerId;
  label: string;
  swatch: string;
  group: LayerGroup;
  describe: string;
}[] = [
  // ─── Municipality ──────────────────────────────────────────────────────
  { id: "municipality-boundary",  label: "Municipal boundary",        swatch: "#0EA5E9", group: "municipality",
    describe: "Yala City Municipality outer boundary (เทศบาลนครยะลา) — the three concentric ring roads radiating from Kilometre Zero." },
  { id: "municipality-buildings", label: "Buildings (OSM + MS, 3D)",  swatch: "#0EA5E9", group: "municipality",
    describe: "Building footprints — OSM/Bing named landmarks (hospitals, temples, hotels) plus Microsoft Building Footprints for the full fabric. Height from levels tag or 2-storey default. Colour-coded by type in 3D." },
  { id: "tile3d-buildings",       label: "Buildings (3D Tiles)",       swatch: "#7DD3FC", group: "municipality",
    describe: "Experimental OGC 3D Tiles stream for Yala City city centre (central circle). Toggle on to compare against the GeoJSON layer." },

  { id: "distance-grid",          label: "Distance grid (1·5·10 km)",  swatch: "#0EA5E9", group: "municipality",
    describe: "Concentric rings at 1 km / 5 km / 10 km from the municipal centroid. Reads as 'how far emergency services can reach from Kilometre Zero'." },

  // ─── Mobility ──────────────────────────────────────────────────────────
  { id: "road-network",      label: "Road network (classified)", swatch: "#FB7185", group: "mobility",
    describe: "OSM road network coloured + scaled by class: motorway/primary thick warm, secondary/tertiary medium cyan, residential/lane thin." },
  { id: "traffic-heatmap",   label: "Traffic — by hour",       swatch: "#F87171", group: "mobility",
    describe: "Modelled traffic intensity weighted by hour-of-day + weekday/weekend. Drives the orange/red glow on arterials." },
  { id: "transit-stations",  label: "Bus / transit stops",     swatch: "#38BDF8", group: "mobility",
    describe: "Yala and Ban Yupo SRT stations plus the Betong road node, from OSM inside the municipality bbox." },
  { id: "transit-lines",     label: "Southern Line (SRT)",     swatch: "#057B43", group: "mobility",
    describe: "State Railway of Thailand Southern Line through Yala — running track, station yard and sidings. Flood closures on the Hat Yai – Yala segment are a real Deep South event." },
  { id: "cctv-cameras",      label: "CCTV cameras",            swatch: "#E5E7EB", group: "mobility",
    describe: "Public traffic cameras from Longdo inside the Yala bbox. Click for the live JPG/HLS stream." },

  // ─── Incidents ─────────────────────────────────────────────────────────
  { id: "incidents-itic",    label: "iTIC traffic events",     swatch: "#F59E0B", group: "incidents",
    describe: "Live iTIC / Longdo traffic events: accidents, closures, breakdowns. Bbox-filtered to Yala municipality." },
  { id: "incidents-city-reports", label: "Citizen reports (Traffy)", swatch: "#A78BFA", group: "incidents",
    describe: "Live citizen complaints from Traffy Fondue — Thailand's nationwide 311 channel." },

  // ─── Open data ─────────────────────────────────────────────────────────
  { id: "datago-points",     label: "data.go.th points",        swatch: "#C084FC", group: "open-data",
    describe: "Government POIs from data.go.th filtered to Yala: schools, hospitals, health centres, government offices, temples, markets." },

  // ─── Civic (OSM, province-wide) ────────────────────────────────────────
  { id: "civic-points",      label: "Civic POIs (color-coded)", swatch: "#EF4444", group: "municipality",
    describe: "Hospitals (✚ red) · clinics (pink) · schools (SC violet) · police (P cyan) · fire stations (FI orange) · government (cerulean) · temples (卐 gold) · markets (▦ green) · post offices · substations · water works. Hover for name. From OSM province-wide." },
  { id: "waterways",         label: "Canals + rivers + drains",  swatch: "#0EA5E9", group: "municipality",
    describe: "Hydrology network: rivers (sky blue, thick), canals (cerulean, medium), streams (pale sky, thin), drains/ditches (teal). Critical for flood-prevention planning + identifying drainage backbone." },
  { id: "flood-risk-zones",  label: "River flood-risk zones",     swatch: "#EF4444", group: "environment",
    describe: "Coarse SRTM/OSM terrain-connected river-rise scenario. Adjust +0 to +8 m relative to local DEM river reference; not gauge metres, observed inundation, calibrated depth or a forecast. Amber walls mark building centroids in wet cells." },

  { id: "air4thai-stations", label: "Air4Thai PCD stations",     swatch: "#22C55E", group: "environment",
    describe: "Official Thai government air-quality monitors for Yala Province — stations at Yala City and Betong. Coloured by PM2.5, labelled with live national AQI. Public source, no key. Click a station for its readings." },

  // ─── GISTDA ────────────────────────────────────────────────────────────
  { id: "gistda-pois",       label: "GISTDA POI Digital Twin",   swatch: "#F59E0B", group: "open-data",
    describe: "Thailand GISTDA POI Digital Twin — 1,000 authoritative points: government offices, schools, temples, hospitals, hotels, banks, restaurants, shopping, transport, sport. Thai + English names, disabled-access flags." },
  { id: "gistda-solar",      label: "GISTDA Solar LOD2",         swatch: "#FBBF24", group: "environment",
    describe: "GISTDA LOD2 building solar irradiance for Yala City centre — real measured height, footprint area, and monthly solar potential (kWh/m²). Blue=low, green=medium, yellow=high, red=excellent rooftop solar." },
  { id: "gistda-landuse",    label: "GISTDA Land Use",           swatch: "#34D399", group: "open-data",
    describe: "GISTDA land use / land cover classification for Yala City — residential, commercial, industrial, agricultural, forest, water, transport, recreation. Colour-coded by category." },

  // ─── News ──────────────────────────────────────────────────────────────
  { id: "news-pins",         label: "Geocoded news pins",        swatch: "#EF4444", group: "incidents",
    describe: "News headlines that mention a known location (market, mosque, hospital, etc.) are pinned on the map so the mayor can see exactly where the incident was." },

  // ─── Deep South Security ─────────────────────────────────────────────────
  { id: "conflict-incidents", label: "Conflict incidents (ACLED)", swatch: "#DC2626", group: "security",
    describe: "Geocoded violent-conflict events — bombings/IEDs, shootings, armed clashes, arson, raids — from ACLED (the machine-readable proxy for the Deep South Incident Database / Deep South Watch). Scoped to Yala + Pattani + Narathiwat. Sized by fatalities, coloured by event type. Requires ACLED_API_KEY; scenario sample otherwise." },
  { id: "security-news",      label: "Security-tagged news",      swatch: "#F87171", group: "security",
    describe: "News items tagged SEC (insurgency / security) pinned to their known location — a fast read on where the latest Deep South incidents are being reported." },
  { id: "conflict-choropleth", label: "Conflict intensity (aggregate)", swatch: "#DC2626", group: "security",
    describe: "AGGREGATE conflict intensity by province/district — choropleth shading, never individual incident points (per Deep South ethical-display guidance). Yala ≈28% / Pattani ≈33% / Narathiwat ≈36% of Deep South incidents since 2004. Monthly, neutral framing." },
  { id: "poverty-choropleth", label: "MPI poverty (TPMAP)",        swatch: "#A855F7", group: "environment",
    describe: "Multidimensional Poverty Index by district from TPMAP — Yala province 20.83% / 208,274 persons below the poverty line. The highest-value outcome layer absent from the live municipal dashboard." },

  // ─── Flood / hydrology ───────────────────────────────────────────────────
  { id: "river-buffer",      label: "Pattani River corridor",    swatch: "#0EA5E9", group: "environment",
    describe: "The Pattani River centreline and inundation buffer running along the western edge of the circular core — the primary flood vector for Yala city." },
  { id: "flood-gauges",      label: "River / canal gauges",      swatch: "#38BDF8", group: "environment",
    describe: "Real named water-level gauges on the Pattani River and feeder canals near Yala city, coloured by status (normal / warning / flood). Source: FloodDash (RID/HII telemetry); falls back to an Open-Meteo discharge proxy if unreachable." },
  { id: "dam-status",        label: "Bang Lang Dam status",      swatch: "#22D3EE", group: "environment",
    describe: "Bang Lang Dam (upstream in Bannang Sata) — real storage %, outflow, spill status from FloodDash telemetry. Rising outflow precedes downstream flooding in Yala city." },
  { id: "shelters",          label: "Emergency shelters",         swatch: "#F97316", group: "environment",
    describe: "DDPM-registered emergency/flood shelters near Yala — capacity, power/water/toilet availability, coordinator phone. Source: FloodDash (DDPM GD002 registry)." },
  { id: "municipal-flood-cameras", label: "Flood infrastructure cameras", swatch: "#2563EB", group: "environment",
    describe: "Municipal cameras watching flood-response assets \u2014 the three drainage pumps (PA/PB/PC) and the Baan Rom flood shelter house. Two sit at Tha Sap Bridge, ~200 m from the river gauges. A hollow marker with a red ring means the camera is not reporting; open the linked viewer for live video." },
  { id: "flood-affected-districts", label: "Flood-affected areas", swatch: "#DC2626", group: "environment",
    describe: "Flood-affected villages by district from the Yala Provincial Office situation report (data.go.th). Bubble size + colour scale with the number of affected villages. Approximate district centroids. Hover for the count." },
  { id: "alphaearth-landcover", label: "AlphaEarth land cover",  swatch: "#34D399", group: "environment",
    describe: "Google DeepMind AlphaEarth Foundations embeddings classified into land cover (rubber plantation vs forest vs built-up vs water) for the Yala basin. Pre-computed from Earth Engine; ships as static GeoJSON." },
  { id: "alphaearth-floodprone", label: "Legacy susceptibility sketch", swatch: "#60A5FA", group: "environment",
    describe: "Unvalidated heuristic polygons from the original prototype, not AlphaEarth embeddings or observed flooding. Use the terrain scenario for coarse geographic screening." },

  // ─── Circular-city signature ───────────────────────────────────────────────
  { id: "ring-roads",        label: "Ring roads (Wongwian)",     swatch: "#FBBF24", group: "municipality",
    describe: "Yala's defining feature — the concentric ring roads (Wongwian 1/2/3) and radial spokes that fan out from the central roundabout / Kilometre Zero. Thailand's only fully planned circular city." },

  // ─── Imagery ───────────────────────────────────────────────────────────
  { id: "satellite-esri",    label: "Satellite (Esri HD)",      swatch: "#60A5FA", group: "imagery",
    describe: "Esri World Imagery — high-res aerial / satellite mosaic. Good detail up to zoom 19." },
  { id: "google-satellite",  label: "Satellite (Google HD)",    swatch: "#34D399", group: "imagery",
    describe: "Google Maps satellite imagery via the Map Tiles API — often more recent and higher-res than Esri. Requires a Google Maps key." },
  { id: "google-traffic",    label: "Live Traffic (Google)",    swatch: "#F87171", group: "imagery",
    describe: "Google real-time traffic overlay (Map Tiles API). Dense over Bangkok and major corridors; provincial roads may show little flow data. Requires a Google Maps key." },
  { id: "satellite-terrain", label: "OpenTopoMap (zoom < 14)",  swatch: "#A3E635", group: "imagery",
    describe: "OpenTopoMap with contour lines and hillshade. Useful at regional zoom." },
  { id: "satellite-viirs-truecolor", label: "VIIRS true-color", swatch: "#A5F3FC", group: "imagery",
    describe: "VIIRS NOAA-20 corrected reflectance — daily, sharper than MODIS." },
  { id: "satellite-night",   label: "VIIRS night lights",       swatch: "#FACC15", group: "imagery",
    describe: "VIIRS Day/Night Band — Earth at night. Yala city core glows against the dark surrounding forest and mountains." },
  { id: "satellite-true-color", label: "MODIS true-color",      swatch: "#93C5FD", group: "imagery",
    describe: "MODIS Terra corrected reflectance — daily 250 m global mosaic. Best at zoom < 10." },
  { id: "satellite-himawari", label: "Himawari IR",             swatch: "#C7D2FE", group: "imagery",
    describe: "Himawari-9 Band 13 infrared — geostationary cloud loop, 10-min refresh. Best for monsoon fronts over the Gulf." },
  { id: "satellite-imerg",   label: "IMERG rainfall",           swatch: "#06B6D4", group: "imagery",
    describe: "NASA IMERG half-hourly global precipitation rate. Watch monsoon cells move across the Gulf." },
  { id: "satellite-ndvi",    label: "NDVI greenery",            swatch: "#34D399", group: "imagery",
    describe: "MODIS NDVI 8-day composite — vegetation greenness." },
  { id: "satellite-lst",     label: "Land surface temp",        swatch: "#F97316", group: "imagery",
    describe: "MODIS LST (day) — urban heat island. Yala city core typically reads hotter than the surrounding rubber plantations and forest." },
  { id: "satellite-aerosol", label: "Aerosol optical depth",    swatch: "#F472B6", group: "imagery",
    describe: "MODIS MAIAC AOD — proxy for PM2.5 + haze / agricultural burning smoke over the Deep South." },
  { id: "satellite-no2",     label: "NO₂ pollution (OMI)",      swatch: "#EF4444", group: "imagery",
    describe: "OMI tropospheric NO₂ — traffic + power-plant nitrogen dioxide. Relatively low in Yala compared to industrial corridors." },
];

/**
 * Layers whose render output is computed or decorative — no backing FeatureCollection,
 * so the per-layer "feature count" badge is meaningless (always 0) and misleads operators
 * into thinking the data feed has failed. The palette suppresses the count badge for
 * these IDs and falls back to a plain "on/off" indicator.
 *
 * Includes: programmatic geometry (distance grid), supplementary 3D extrusions
 * (building roofs), and all raster tile layers (satellite imagery, 3D tiles).
 */
export const COMPUTED_LAYERS: ReadonlySet<LayerId> = new Set<LayerId>([
  "distance-grid",
  "building-roofs",
  "tile3d-buildings",
  "heritage-old-town",
  "heritage-temple-spires",
  // All GIBS + Esri + Google satellite rasters
  "satellite-esri",
  "google-satellite",
  "google-traffic",
  "satellite-true-color",
  "satellite-viirs-truecolor",
  "satellite-night",
  "satellite-ndvi",
  "satellite-lst",
  "satellite-aerosol",
  "satellite-no2",
  "satellite-terrain",
  "satellite-himawari",
  "satellite-imerg",
]);
